'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowUpIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { Drawer } from 'vaul'
import { cn } from '~/utils/cn'
import { charterSections } from '../_lib/sections'

type Section = (typeof charterSections)[number]

type ChildSection = Section & {
	children?: readonly {
		id: string
		label: string
	}[]
}

const flattenIds = (sections: readonly ChildSection[]): string[] => {
	return sections.reduce<string[]>((acc, section) => {
		acc.push(section.id)
		if ('children' in section && section.children) {
			acc.push(...flattenIds(section.children as readonly ChildSection[]))
		}

		return acc
	}, [])
}

const allIds = flattenIds(charterSections as readonly ChildSection[])

const findLabelById = (
	id: string,
	sections: readonly ChildSection[],
): string | undefined => {
	for (const section of sections) {
		if (section.id === id) {
			return section.label
		}
		if ('children' in section && section.children) {
			const found = findLabelById(
				id,
				section.children as readonly ChildSection[],
			)
			if (found) {
				return found
			}
		}
	}

	return undefined
}

const findTopLevelLabel = (
	id: string,
	sections: readonly ChildSection[],
): string | undefined => {
	for (const section of sections) {
		if (section.id === id) {
			return section.label
		}
		if ('children' in section && section.children) {
			const found = findLabelById(
				id,
				section.children as readonly ChildSection[],
			)
			if (found) {
				return section.label
			}
		}
	}

	return undefined
}

const SCROLL_THRESHOLD = 200

const useScrolledPastTop = () => {
	const [
		isScrolled,
		setIsScrolled,
	] = useState(false)

	useEffect(() => {
		const handleScroll = () => {
			setIsScrolled(window.scrollY > SCROLL_THRESHOLD)
		}

		handleScroll()
		window.addEventListener('scroll', handleScroll, {
			passive: true,
		})

		return () => {
			return window.removeEventListener('scroll', handleScroll)
		}
	}, [])

	return isScrolled
}

const useFooterDocking = () => {
	const [
		bottomOffset,
		setBottomOffset,
	] = useState(16)

	useEffect(() => {
		const footer = document.querySelector('footer')
		if (!footer) {
			return
		}

		const updatePosition = () => {
			const footerRect = footer.getBoundingClientRect()
			const viewportHeight = window.innerHeight
			const defaultBottom = 16

			if (footerRect.top < viewportHeight) {
				const footerVisibleHeight = viewportHeight - footerRect.top
				setBottomOffset(footerVisibleHeight + defaultBottom)
			} else {
				setBottomOffset(defaultBottom)
			}
		}

		updatePosition()
		window.addEventListener('scroll', updatePosition, {
			passive: true,
		})
		window.addEventListener('resize', updatePosition, {
			passive: true,
		})

		return () => {
			window.removeEventListener('scroll', updatePosition)
			window.removeEventListener('resize', updatePosition)
		}
	}, [])

	return {
		bottomOffset,
	}
}

const DesktopTocLink = ({
	id,
	label,
	activeId,
	depth,
	onClick,
}: {
	id: string
	label: string
	activeId: string
	depth: number
	onClick: () => void
}) => {
	const isActive = activeId === id

	return (
		<a
			href={`#${id}`}
			onClick={(e) => {
				e.preventDefault()
				const el = document.getElementById(id)
				if (el) {
					el.scrollIntoView({
						behavior: 'smooth',
					})
					window.history.replaceState(null, '', `#${id}`)
				}
				onClick()
			}}
			className={cn(
				'relative block py-1 text-sm transition-colors',
				depth === 0 && 'pl-3',
				depth === 1 && 'pl-7',
				depth >= 2 && 'pl-11',
				isActive
					? 'text-zephyr-blue'
					: 'text-zephyr-gray-light hover:text-zephyr-light',
			)}
		>
			{isActive ? (
				<motion.div
					layoutId="toc-active-indicator"
					className="absolute top-0 bottom-0 left-0 w-0.5 bg-zephyr-blue rounded-full"
					transition={{
						type: 'spring',
						stiffness: 350,
						damping: 30,
					}}
				/>
			) : null}
			{label}
		</a>
	)
}

const DesktopNavItems = ({
	sections,
	activeId,
	depth,
	onClick,
}: {
	sections: readonly ChildSection[]
	activeId: string
	depth: number
	onClick: () => void
}) => {
	return (
		<>
			{sections.map((section) => {
				return (
					<div key={section.id}>
						<DesktopTocLink
							id={section.id}
							label={section.label}
							activeId={activeId}
							depth={depth}
							onClick={onClick}
						/>
						{'children' in section && section.children ? (
							<DesktopNavItems
								sections={section.children as readonly ChildSection[]}
								activeId={activeId}
								depth={depth + 1}
								onClick={onClick}
							/>
						) : null}
					</div>
				)
			})}
		</>
	)
}

const DrawerNavItems = ({
	sections,
	activeId,
	depth,
	onNavigate,
}: {
	sections: readonly ChildSection[]
	activeId: string
	depth: number
	onNavigate: (id: string) => void
}) => {
	return (
		<>
			{sections.map((section) => {
				const isActive = activeId === section.id

				return (
					<div key={section.id}>
						<button
							type="button"
							onClick={() => {
								return onNavigate(section.id)
							}}
							className={cn(
								'w-full rounded-lg text-left text-sm transition-colors',
								depth === 0 && 'px-3 py-2',
								depth === 1 && 'px-3 py-1.5 pl-7',
								depth >= 2 && 'px-3 py-1.5 pl-11',
								isActive
									? 'bg-zephyr-blue/10 font-medium text-zephyr-blue'
									: 'text-zephyr-gray-light hover:bg-surface-elevated hover:text-zephyr-light',
							)}
						>
							{section.label}
						</button>
						{'children' in section && section.children ? (
							<DrawerNavItems
								sections={section.children as readonly ChildSection[]}
								activeId={activeId}
								depth={depth + 1}
								onNavigate={onNavigate}
							/>
						) : null}
					</div>
				)
			})}
		</>
	)
}

const TableOfContents = () => {
	const [
		activeId,
		setActiveId,
	] = useState<string>(allIds[0] ?? '')
	const [
		drawerOpen,
		setDrawerOpen,
	] = useState(false)
	const observerRef = useRef<IntersectionObserver | null>(null)
	const isScrolled = useScrolledPastTop()
	const {
		bottomOffset,
	} = useFooterDocking()

	const handleDesktopClick = useCallback(() => {
		// no-op for desktop, kept for consistency
	}, [])

	const handleDrawerNavigate = useCallback((id: string) => {
		setDrawerOpen(false)
		setTimeout(() => {
			const el = document.getElementById(id)
			if (el) {
				const offset = 80
				const top = el.getBoundingClientRect().top + window.scrollY - offset
				window.scrollTo({
					top,
					behavior: 'smooth',
				})
				window.history.replaceState(null, '', `#${id}`)
			}
		}, 300)
	}, [])

	const scrollToTop = useCallback(() => {
		setDrawerOpen(false)
		setTimeout(() => {
			window.scrollTo({
				top: 0,
				behavior: 'smooth',
			})
		}, 150)
	}, [])

	useEffect(() => {
		observerRef.current = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (entry.isIntersecting) {
						setActiveId(entry.target.id)
					}
				}
			},
			{
				rootMargin: '0px 0px -70% 0px',
			},
		)

		const observer = observerRef.current

		for (const id of allIds) {
			const el = document.getElementById(id)
			if (el) {
				observer.observe(el)
			}
		}

		return () => {
			observer.disconnect()
		}
	}, [])

	const activeLabel = findLabelById(activeId, charterSections as readonly ChildSection[]) ?? ''
	const topLevelLabel = findTopLevelLabel(activeId, charterSections as readonly ChildSection[])

	return (
		<div>
			{/* Desktop sticky sidebar */}
			<nav
				aria-label="Table of contents"
				className="sticky top-0 hidden pt-6 lg:block"
			>
				<p className="mb-3 text-xs font-semibold uppercase tracking-wider text-zephyr-gray-light">
					On this page
				</p>
				<div className="relative border-l border-zephyr-gray/30">
					<DesktopNavItems
						sections={charterSections as readonly ChildSection[]}
						activeId={activeId}
						depth={0}
						onClick={handleDesktopClick}
					/>
				</div>

				<AnimatePresence>
					{isScrolled ? (
						<motion.button
							type="button"
							initial={{
								opacity: 0,
								y: 5,
							}}
							animate={{
								opacity: 1,
								y: 0,
							}}
							exit={{
								opacity: 0,
								y: 5,
							}}
							transition={{
								duration: 0.2,
							}}
							onClick={() => {
								window.scrollTo({
									top: 0,
									behavior: 'smooth',
								})
							}}
							className="mt-4 flex items-center gap-1.5 text-zephyr-gray-light text-xs transition-colors hover:text-zephyr-light"
						>
							<ArrowUpIcon className="size-3" />
							<span>
								Back to top
							</span>
						</motion.button>
					) : null}
				</AnimatePresence>
			</nav>

			{/* Mobile: Bottom bar with Vaul drawer */}
			<Drawer.Root
				open={drawerOpen}
				onOpenChange={setDrawerOpen}
			>
				<Drawer.Trigger asChild={true}>
					<button
						type="button"
						className="fixed right-4 left-4 z-40 flex items-center justify-between rounded-xl border border-zephyr-gray/30 bg-surface/95 px-4 py-3 shadow-lg backdrop-blur-sm transition-[bottom] duration-150 ease-out lg:hidden"
						style={{
							bottom: bottomOffset,
						}}
					>
						<div className="flex flex-col items-start">
							{topLevelLabel && topLevelLabel !== activeLabel ? (
								<span className="text-zephyr-gray-light text-xs">
									{topLevelLabel}
								</span>
							) : null}
							<span className="font-medium text-zephyr-light text-sm">
								{activeLabel}
							</span>
						</div>
						<ChevronUpIcon className="h-5 w-5 shrink-0 text-zephyr-gray-light" />
					</button>
				</Drawer.Trigger>

				<Drawer.Portal>
					<Drawer.Overlay className="fixed inset-0 z-50 bg-black/40" />
					<Drawer.Content className="fixed right-0 bottom-0 left-0 z-50 mt-24 flex max-h-[85vh] flex-col rounded-t-2xl bg-surface">
						<div className="mx-auto mt-3 h-1.5 w-12 shrink-0 rounded-full bg-zephyr-gray/50" />

						<Drawer.Title className="sr-only">
							Table of Contents
						</Drawer.Title>
						<Drawer.Description className="sr-only">
							Navigate to sections on this page
						</Drawer.Description>

						<div className="overflow-y-auto p-6">
							<h3 className="mb-3 font-semibold text-zephyr-light text-sm">
								On this page
							</h3>
							<div className="flex flex-col gap-0.5">
								<DrawerNavItems
									sections={charterSections as readonly ChildSection[]}
									activeId={activeId}
									depth={0}
									onNavigate={handleDrawerNavigate}
								/>
							</div>

							{isScrolled ? (
								<button
									type="button"
									onClick={scrollToTop}
									className="mt-6 flex w-full items-center justify-center gap-1.5 rounded-lg border border-zephyr-gray/30 py-2.5 text-zephyr-gray-light text-sm transition-colors hover:bg-surface-elevated hover:text-zephyr-light"
								>
									<ArrowUpIcon className="size-4" />
									<span>
										Back to top
									</span>
								</button>
							) : null}
						</div>
					</Drawer.Content>
				</Drawer.Portal>
			</Drawer.Root>
		</div>
	)
}

export default TableOfContents
